import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertCircle, Check, ExternalLink, FlaskConical } from "lucide-react";
import { Alert, Button, Combobox, Input, SimpleSelectPrimitive, Tooltip } from "@/components/ui";
import {
  apiErrorMessage,
  FanServices,
  FormRoutePreview,
  FormRouteServices,
  isApiError,
  PlayerReviewLink,
  PlayersServices,
} from "@/services/bedrock";
import { BuilderState, buildRouteUrl, FieldKey, ReviewSites, routeParams } from "../useFormRouteBuilder";
import { PlayerOption, usePlayerOptions } from "./FormCardWhoAsks";
import { RouteCard } from "./RouteCard";

export const NEW_CLIENT_SAMPLE = { client_id: "C-20991", client_email: "jordan.rivera@example.com", lang: "es" };

const REVIEW_SITE_NAMES: Record<PlayerReviewLink["socialNetworkId"], string> = {
  GL: "Google",
  ZL: "Zillow",
  FB: "Facebook",
};

/** The selected player's review sites, fetched when a player is picked, so the admin sees the real links. */
function PlayerReviewLinks({ playerId, playerName }: { playerId: number; playerName: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["playerReviewLinks", playerId],
    queryFn: () => PlayersServices.getReviewLinks(playerId),
  });
  const links = data?.content ?? [];

  let body: React.ReactNode;
  if (isLoading) {
    body = <span className="text-slate-400">Checking review links…</span>;
  } else if (isError) {
    body = <span className="text-slate-500">We couldn't load their review links.</span>;
  } else if (!links.length) {
    body = <span>None connected, so the review is collected on the form.</span>;
  } else {
    body = (
      <span className="flex flex-wrap items-center gap-1.5">
        {links.map(link => (
          <a
            key={link.socialNetworkId}
            href={link.destinationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:border-primary hover:text-primary"
          >
            {REVIEW_SITE_NAMES[link.socialNetworkId] ?? link.socialNetworkId}
            <ExternalLink className="size-3" />
          </a>
        ))}
      </span>
    );
  }

  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600"
      data-testid="player-review-links"
    >
      <span className="font-semibold text-slate-700">{playerName}'s review links:</span>
      {body}
    </div>
  );
}

interface Props {
  state: BuilderState;
  routeRef: string | null;
  routeUrl: string | null;
  formTypeLabel: string;
  accountDefaultFormId: number | null;
  disabled: boolean;
  /** Whether the form opens review sites; the selected player's links only matter when it might. */
  reviewSites: ReviewSites;
}

interface Row {
  label: string;
  ok: boolean;
  text: string;
}

export function previewRows(
  preview: FormRoutePreview,
  context: {
    playerMode: BuilderState["playerMode"];
    playerName: string | null;
    partner: boolean;
    accountDefaultFormId: number | null;
  }
): Row[] {
  const rows: Row[] = [];
  const name = preview.player.displayName || context.playerName || "This player";

  if (context.playerMode === "none" || preview.player.resolution === "absent") {
    rows.push({ label: "Player", ok: true, text: "None (account-level link)" });
  } else {
    const player = {
      matched: { ok: true, text: `${name}, matched by email` },
      not_found: {
        ok: false,
        text: "No match. The link would open at the account level. Check the email, or add it as an alias on the player.",
      },
      ineligible: {
        ok: false,
        text: `${name} isn't eligible for this form. The link would open at the account level.`,
      },
      from_fan: { ok: true, text: "Taken from the Fan's record" },
      not_visible: { ok: false, text: "Not in your branches" },
    }[preview.player.resolution];
    rows.push({ label: "Player", ...player });
  }

  let formText = preview.form.displayName;
  if (preview.form.via === "default") {
    formText +=
      preview.form.formId === context.accountDefaultFormId ? " (account-wide default)" : " (region or branch default)";
  }
  rows.push({ label: "Form", ok: true, text: formText });

  const fanName = preview.fan.displayName || "A Fan with no name yet";
  const fan = {
    matched_fan: `${fanName}, matched by Fan ID`,
    matched_client_id: `${fanName}, matched by ID`,
    matched_internal_identifier: `${fanName}, matched by ID`,
    matched_email: `${fanName}, matched by email`,
    created: `A new Fan would be created${context.partner ? " as a partner" : ""} and asked for their name on the form`,
    none: "None. The form will ask for their details.",
  }[preview.fan.resolution];
  rows.push({ label: "Fan", ok: true, text: fan });

  rows.push({
    label: "Form link",
    ok: true,
    text:
      preview.request.action === "would_reuse"
        ? preview.fan.displayName
          ? `Already exists for ${preview.fan.displayName}. It would be reused.`
          : "Already exists. It would be reused."
        : "Would be created",
  });
  return rows;
}

export function RouteTester({
  state,
  routeRef,
  routeUrl,
  formTypeLabel,
  accountDefaultFormId,
  disabled,
  reviewSites,
}: Props) {
  const players = usePlayerOptions(null);
  const [sampleChoice, setSampleChoice] = useState<"existing" | "new" | null>(null);

  const { data: fanPage } = useQuery({
    queryKey: ["formRoutes", "sampleFan"],
    queryFn: () => FanServices.getFans({ perPage: 1, active: true }),
  });
  const sampleFan = fanPage?.content?.[0] ?? null;
  const sample = sampleChoice ?? (sampleFan ? "existing" : "new");

  const testerPlayer = (players.value as PlayerOption | null)?.player ?? null;
  const sampleValues = useMemo<Record<FieldKey, string>>(() => {
    const player = state.playerMode === "sender" ? testerPlayer?.email ?? "" : "";
    if (sample === "existing" && sampleFan) {
      return {
        player,
        fan: sampleFan.guid ?? "",
        client_id: sampleFan.internalIdentifier ?? "",
        client_email: sampleFan.email ?? "",
        lang: sampleFan.defaultLanguage ?? "",
      };
    }
    return { player, fan: "", ...NEW_CLIENT_SAMPLE };
  }, [state.playerMode, testerPlayer?.email, sample, sampleFan]);
  const params = routeParams(state, sampleValues);

  const run = useMutation({
    mutationFn: () => FormRouteServices.preview("forms", routeRef!, Object.fromEntries(params)),
  });

  // A result describes the route it ran; once the route changes, it and its test link would mislead
  const routeKey = JSON.stringify([routeRef, params]);
  const { reset: resetRun } = run;
  useEffect(() => resetRun(), [routeKey, resetRun]);

  const errorText = (() => {
    if (!run.error) return null;
    if (isApiError(run.error, "form_not_found")) return `No ${formTypeLabel} form is set up yet. Choose one in step 1.`;
    if (isApiError(run.error, "feature_disabled")) return "Reputation Management isn't enabled on this account.";
    return apiErrorMessage(run.error, "The test couldn't run. Try again.");
  })();

  const selectedPlayerId = state.playerMode === "specific" ? state.specificPlayer?.id : testerPlayer?.id;
  const playerName =
    state.playerMode === "specific"
      ? state.specificPlayer?.fullName || state.specificPlayer?.email || null
      : testerPlayer?.fullName || testerPlayer?.email || null;

  const sampleItems = [
    ...(sampleFan
      ? [{ value: "existing", label: `Existing Fan: ${sampleFan.displayName || sampleFan.email || "unnamed"}` }]
      : []),
    { value: "new", label: "New client (sample details)" },
  ];
  const sampleHint =
    sample === "existing" && sampleFan
      ? `One of your Fans, sent the way your CRM would${
          sampleFan.internalIdentifier ? ` (Client ID ${sampleFan.internalIdentifier})` : ""
        }, to check they're found.`
      : `Made-up details (Client ID ${NEW_CLIENT_SAMPLE.client_id}) for someone you don't have yet.`;

  return (
    <RouteCard
      title="Try it with a real player"
      description="Runs the same lookup a click would, without creating anything. A sample client is filled in for you."
      testId="route-tester"
    >
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-3">
        {state.playerMode === "sender" && (
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">Player</span>
            <Combobox {...players} placeholder="Search for a player..." className="w-full" />
          </div>
        )}
        {state.playerMode === "specific" && (
          <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
            Player
            <Input value={playerName ?? ""} readOnly disabled placeholder="Choose a player in Who's asking" />
          </label>
        )}
        <div
          className={
            state.playerMode === "none"
              ? "flex min-w-0 flex-col gap-1.5 sm:col-span-2"
              : "flex min-w-0 flex-col gap-1.5"
          }
        >
          <span className="text-sm font-medium text-slate-700">Sample client</span>
          <div
            className="min-w-0 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm"
            data-testid="sample-client"
          >
            <SimpleSelectPrimitive
              value={sample}
              onValueChange={value => setSampleChoice((value as "existing" | "new") ?? null)}
            >
              <SimpleSelectPrimitive.Trigger className="w-full min-w-0 justify-between">
                <SimpleSelectPrimitive.Value className="block min-w-0 truncate">
                  {sampleItems.find(item => item.value === sample)?.label}
                </SimpleSelectPrimitive.Value>
              </SimpleSelectPrimitive.Trigger>
              <SimpleSelectPrimitive.Content>
                {sampleItems.map(item => (
                  <SimpleSelectPrimitive.Item key={item.value} value={item.value}>
                    {item.label}
                  </SimpleSelectPrimitive.Item>
                ))}
              </SimpleSelectPrimitive.Content>
            </SimpleSelectPrimitive>
          </div>
        </div>
        <Button variant="outline" onClick={() => run.mutate()} loading={run.isPending} disabled={disabled || !routeRef}>
          <FlaskConical className="mr-1.5 h-4 w-4" />
          Run test
        </Button>
      </div>

      <p className="-mt-2 text-xs text-slate-500" data-testid="sample-hint">
        {sampleHint}
      </p>

      {reviewSites !== "no" && selectedPlayerId && (
        <PlayerReviewLinks playerId={selectedPlayerId} playerName={playerName ?? "The player"} />
      )}

      {run.isSuccess && routeUrl && routeRef && (
        <div>
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button
                  variant="secondary"
                  onClick={() =>
                    window.open(buildRouteUrl(routeUrl, routeRef, [...params, ["test", "1"]]), "_blank", "noopener")
                  }
                >
                  <ExternalLink className="mr-1.5 h-4 w-4" />
                  Open as a test link
                </Button>
              }
            />
            <Tooltip.Content>
              Opens the route with these values as a test link. The Fan and link are marked as tests.
            </Tooltip.Content>
          </Tooltip>
        </div>
      )}

      {errorText && (
        <Alert variant="destructive" data-testid="tester-error">
          <AlertCircle />
          <Alert.Description>{errorText}</Alert.Description>
        </Alert>
      )}

      {run.isSuccess && run.data && (
        <ul className="flex flex-col divide-y divide-slate-100" data-testid="tester-result">
          {previewRows(run.data, {
            playerMode: state.playerMode,
            playerName,
            partner: state.clientType === "partner",
            accountDefaultFormId,
          }).map(row => (
            <li key={row.label} className="flex items-start gap-2.5 py-2 text-sm">
              {row.ok ? (
                <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" />
              ) : (
                <AlertCircle className="mt-0.5 size-4 shrink-0 text-amber-600" />
              )}
              <span className="w-20 shrink-0 font-semibold text-slate-700">{row.label}</span>
              <span className="text-slate-600">{row.text}</span>
            </li>
          ))}
        </ul>
      )}
    </RouteCard>
  );
}
